import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BirdzTitle } from '@applications-terrains/birdz-react-library';
import { Autocomplete, Box, Grid, TextField } from '@mui/material';
import PagePaper from '../../../Tools/Pages/PagePaper';
import { useFetchDataRefsContext } from '../../../../contexts/FetchDataRefsContext';
import { equipmentConfig } from '../params.config';
// import Indicators from '../Indicators';
// import Simulator from '../Simulator';
import ConfigurationsTable from './ConfigurationTable';
import DefaultConfigTable from './DefaultConfigTable';
import FabcompConfigTable from './FabcompConfigTable';
import BSearchForm from '../../../Tools/Search/BSearchForm';
import {
  useGetClientsAndContracts,
  useGetExocetConfigurations,
  useGetExocetDefaultConfigurations,
  useGetExocetFamilies,
  useGetExocetRadioModes
} from '../../../../hooks/datarefs';
import { FormikValues } from 'formik';
import { DefaultRadioConfig, RadioConfig } from './types';
import { MuiLikeBorderedBox } from '../../../Tools/Form/FormFieldWrapper';
import CustomSwitchField from '../../../CustomSwitchField/CustomSwitchField';
import useRefresh from '../../../../hooks/useRefresh';
import { translate } from './utils';
import { formatToSelectOptions } from '../../../../utils';

const EquipementConfiguration = () => {
  const [filterString, setFilterString] = useState<string>('');
  const [createDefaultConfig, setCreateDefaultConfig] = useState<RadioConfig | null>(null);
  const [editDefaultConfig, setEditDefaultConfig] = useState<DefaultRadioConfig | null>(null);
  const { dataRefs } = useFetchDataRefsContext();
  const equipment = equipmentConfig['configuration' as keyof typeof equipmentConfig];
  const { t } = useTranslation();
  const { data: radioModes } = useGetExocetRadioModes();
  const { data: configurationProfiles = [], refetch: refetchProfiles } =
    useGetExocetConfigurations();
  const { data: defaultConfigurations = [], refetch: refetchDefaultConfigurations } =
    useGetExocetDefaultConfigurations();
  const { data: ExocetFamilies } = useGetExocetFamilies();
  // useRefresh garde cb dans ses dépendances : la callback doit être stable
  const refetchAll = useCallback(() => {
    refetchProfiles();
    refetchDefaultConfigurations();
  }, [refetchProfiles, refetchDefaultConfigurations]);
  const { refresh, triggerRefresh } = useRefresh(refetchAll);
  const families =
    ExocetFamilies?.sort((a, b) => String(a.Name).localeCompare(String(b.Name))) || [];
  const { data: clients } = useGetClientsAndContracts();

  const searchFields = [
    {
      name: 'name',
      label: t('equipmentConfiguration.name'),
      searchable: true,
      searchTargetKey: 'name',
      freeSolo: false,
      options: {
        values:
          configurationProfiles?.map((config) => ({
            label: config.name,
            value: config.name
          })) || []
      }
    },
    {
      name: 'dataset_code',
      label: t('equipmentConfiguration.datasetCodeLabel'),
      searchable: true,
      searchTargetKey: 'dataset_code',
      freeSolo: false,
      options: {
        values:
          configurationProfiles?.map((config) => ({
            label: config.dataset_code,
            value: config.dataset_code
          })) || []
      }
    },
    {
      name: 'families__in',
      label: t('equipmentConfiguration.families'),
      multiple: true,
      options: {
        values: formatToSelectOptions(
          families?.map((family) => ({
            Name: translate(family.Name),
            Id: family.Id
          })),
          {
            label: 'Name',
            value: 'Id'
          }
        )
      }
    },
    {
      name: 'radio_mode',
      label: t('equipmentConfiguration.radioMode'),
      options: {
        values: formatToSelectOptions(radioModes, {
          label: 'Label',
          value: 'Id'
        })
      }
    },
    //more filters
    {
      name: 'authorised_customers',
      label: '',
      seeMore: true,
      render: (_: string[], onChange: (fieldName: string, data?: string | number) => void) => (
        <Autocomplete
          size="small"
          options={(clients ?? [])
            ?.filter((client) => client.oss_code)
            .map((client) => ({
              label: client.oss_code,
              value: client.id
            }))}
          onChange={(_, newValue) => {
            onChange('authorised_customers', newValue?.value);
          }}
          isOptionEqualToValue={(option, value) => {
            return option.label === value?.label;
          }}
          renderInput={(params) => (
            <TextField {...params} label={t('equipmentConfiguration.authorizedClients')} />
          )}
          fullWidth
        />
      )
    },
    {
      name: 'authorised_contracts',
      seeMore: true,
      label: '',
      render: (
        _: string[],
        onChange: (fieldName: string, data?: string | number) => void,
        values: any
      ) => (
        <Autocomplete
          disabled={!values?.authorised_customers}
          size="small"
          options={
            (clients ?? [])
              ?.find((client) => client.id === values?.authorised_customers)
              ?.contracts.map((contract) => ({
                label: contract.oss_code,
                value: contract.id
              })) || []
          }
          onChange={(_, newValue) => {
            onChange('authorised_contracts', newValue?.value);
          }}
          isOptionEqualToValue={(option, value) => {
            return option.label === value?.label;
          }}
          renderInput={(params) => (
            <TextField {...params} label={t('equipmentConfiguration.authorizedContracts')} />
          )}
          fullWidth
        />
      )
    },
    {
      name: 'excluded_customers',
      seeMore: true,
      label: '',
      render: (_: string[], onChange: (fieldName: string, data?: string | number) => void) => (
        <Autocomplete
          size="small"
          options={(clients ?? [])
            ?.filter((client) => client.oss_code)
            .map((client) => ({
              label: client.oss_code,
              value: client.id
            }))}
          onChange={(_, newValue) => {
            onChange('excluded_customers', newValue?.value);
          }}
          isOptionEqualToValue={(option, value) => {
            return option.label === value?.label;
          }}
          renderInput={(params) => (
            <TextField {...params} label={t('equipmentConfiguration.excludedClients')} />
          )}
          fullWidth
        />
      )
    },
    {
      name: 'excluded_contracts',
      seeMore: true,
      label: '',
      render: (
        _: string[],
        onChange: (fieldName: string, data?: string | number) => void,
        values: FormikValues
      ) => {
        return (
          <Autocomplete
            disabled={!values?.excluded_customers}
            size="small"
            options={
              (clients ?? [])
                ?.find((client) => client.id === values?.excluded_customers)
                ?.contracts.map((contract) => ({
                  label: contract.oss_code,
                  value: contract.id
                })) || []
            }
            onChange={(_, newValue) => {
              onChange('excluded_contracts', newValue?.value);
            }}
            isOptionEqualToValue={(option, value) => {
              return option.label === value?.label;
            }}
            renderInput={(params) => (
              <TextField {...params} label={t('equipmentConfiguration.excludedContracts')} />
            )}
            fullWidth
          />
        );
      }
    },
    {
      name: 'dst',
      label: '',
      type: 'blank',
      seeMore: true
    },
    {
      name: 'autoconnect',
      label: '',
      type: 'blank',
      seeMore: true
    },
    {
      name: 'hourly_index',
      label: '',
      type: 'blank',
      seeMore: true
    },
    {
      name: 'switchfields',
      label: '',
      seeMore: true,
      render: () => {
        return (
          <MuiLikeBorderedBox sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mt: -2 }}>
            <CustomSwitchField name="autoconnect" label={'Autoconnect'} inForm />
            <CustomSwitchField name="dst" label={'DST'} inForm />
            <CustomSwitchField
              name="hourly_index"
              label={t('equipmentConfiguration.hourly_index')}
              inForm
            />
          </MuiLikeBorderedBox>
        );
      }
    }
  ];

  return (
    <PagePaper
      loader={!(`equipment-configurations` in dataRefs)}
      alwaysShowContent
      title={
        <Grid justifyContent="space-between" alignItems={'center'} container>
          <Grid item>
            <BirdzTitle className="m-0">{t(equipment.label)}</BirdzTitle>
          </Grid>
        </Grid>
      }
    >
      <Box display={'flex'} flexDirection={'column'}>
        {/* <Box sx={{
          display: 'flex', border: '1px solid',
          borderColor: 'divider',
          borderRadius: 1,
          p: 2,
          gap: 2
        }}>
          <Indicators />
          <Divider orientation="vertical" flexItem />
          <Simulator />
        </Box> */}
        <BSearchForm
          columns={2}
          fields={searchFields}
          setSearchString={(filterString) => {
            setFilterString(filterString);
          }}
        />
        <ConfigurationsTable
          config={equipment}
          filterString={filterString}
          moduleFamilies={families}
          radioModes={radioModes}
          setCreateDefaultConfig={setCreateDefaultConfig}
          setEditDefaultConfig={setEditDefaultConfig}
          defaultConfigurations={defaultConfigurations}
          triggerRefreshFromParent={triggerRefresh}
          clients={clients}
        />
        <DefaultConfigTable
          filterString={filterString}
          moduleFamilies={families}
          radioModes={radioModes}
          createDefaultConfig={createDefaultConfig}
          setCreateDefaultConfig={setCreateDefaultConfig}
          editDefaultConfig={editDefaultConfig}
          setEditDefaultConfig={setEditDefaultConfig}
          configurations={configurationProfiles}
          defaultConfigurations={defaultConfigurations}
          triggerRefreshFromParent={triggerRefresh}
          refreshFromParent={refresh}
          clients={clients}
        />
        <FabcompConfigTable filterString={filterString} configurations={configurationProfiles} />
      </Box>
    </PagePaper>
  );
};

export default EquipementConfiguration;
